import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Coins, ChevronDown, ChevronRight, ChevronLeft, Banknote, CreditCard, User2, Store } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/datetime";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtFecha = (iso) => fmtDateTime(iso);

const PAGE_SIZE = 20;
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
// Resuelve el rango [desde, hasta] (YYYY-MM-DD, hora local) a partir del preset seleccionado.
const rangeFromPreset = (preset, cd, ch) => {
  const today = new Date();
  const hasta = toYMD(today);
  if (preset === "hoy") return { desde: hasta, hasta };
  if (preset === "7d" || preset === "15d" || preset === "30d") {
    const days = preset === "7d" ? 6 : preset === "15d" ? 14 : 29;
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return { desde: toYMD(d), hasta };
  }
  if (preset === "custom") return { desde: cd || "", hasta: ch || "" };
  return { desde: "", hasta: "" };
};

export default function Cortes() {
  const [cortes, setCortes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [sucFiltro, setSucFiltro] = useState("all");
  const [preset, setPreset] = useState("7d"); // por defecto: últimos 7 días
  const [customDesde, setCustomDesde] = useState("");
  const [customHasta, setCustomHasta] = useState("");
  const [abierto, setAbierto] = useState(null);

  const { desde, hasta } = rangeFromPreset(preset, customDesde, customHasta);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargar = useCallback(async () => {
    // En rango personalizado, esperar a que ambas fechas estén definidas.
    if (preset === "custom" && (!desde || !hasta)) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sucFiltro !== "all") params.set("id_sucursal", sucFiltro);
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      const { data } = await api.get(`/operativa/cortes?${params.toString()}`);
      setCortes(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudieron cargar los cortes");
    } finally {
      setLoading(false);
    }
  }, [sucFiltro, desde, hasta, page, preset]);

  useEffect(() => {
    listSucursales().then(({ data }) => setSucursales(data || [])).catch(() => {});
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  // Al cambiar sucursal o periodo, regresar a la página 1.
  const onSucChange = (v) => { setSucFiltro(v); setPage(1); setAbierto(null); };
  const onPresetChange = (v) => { setPreset(v); setPage(1); setAbierto(null); };
  const onCustomDesde = (v) => { setCustomDesde(v); setPage(1); setAbierto(null); };
  const onCustomHasta = (v) => { setCustomHasta(v); setPage(1); setAbierto(null); };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-5 lg:p-8" data-testid="cortes-page">
        <div className="mb-6">
          <div className="mb-4">
            <h1 className="font-display text-3xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
              <Coins className="h-7 w-7 text-[#B45309]" /> Cortes de Caja
            </h1>
            <p className="text-sm text-[#52525B] mt-1">Historial de turnos cerrados con su liquidación y gastos registrados.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Sucursal</Label>
              <Select value={sucFiltro} onValueChange={onSucChange}>
                <SelectTrigger className="h-10 rounded-none border-[#0A0A0A] mt-1" data-testid="cortes-suc-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {sucursales.map((s) => (
                    <SelectItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Periodo</Label>
              <Select value={preset} onValueChange={onPresetChange}>
                <SelectTrigger className="h-10 rounded-none border-[#0A0A0A] mt-1" data-testid="cortes-periodo-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoy" data-testid="cortes-periodo-hoy">Hoy</SelectItem>
                  <SelectItem value="7d" data-testid="cortes-periodo-7d">Últimos 7 días</SelectItem>
                  <SelectItem value="15d" data-testid="cortes-periodo-15d">Últimos 15 días</SelectItem>
                  <SelectItem value="30d" data-testid="cortes-periodo-30d">Últimos 30 días</SelectItem>
                  <SelectItem value="custom" data-testid="cortes-periodo-custom">Rango personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {preset === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Desde</Label>
                  <Input type="date" value={customDesde} max={customHasta || undefined} onChange={(e) => onCustomDesde(e.target.value)} data-testid="cortes-desde" className="h-10 rounded-none border-[#0A0A0A] mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Hasta</Label>
                  <Input type="date" value={customHasta} min={customDesde || undefined} onChange={(e) => onCustomHasta(e.target.value)} data-testid="cortes-hasta" className="h-10 rounded-none border-[#0A0A0A] mt-1" />
                </div>
              </div>
            )}
          </div>
        </div>


        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#B45309]" /></div>
        ) : cortes.length === 0 ? (
          <div className="border border-dashed border-[#d4d4d8] py-16 text-center text-[#52525B]" data-testid="cortes-empty">No hay cortes cerrados todavía.</div>
        ) : (
          <div className="space-y-3">
            {cortes.map((c) => {
              const open = abierto === c.id_caja;
              const diff = c.efectivo_cierre != null ? Number(c.efectivo_cierre) - Number(c.efectivo_neto_entregar) : null;
              return (
                <motion.div key={c.id_caja} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="border border-[#e4e4e7] bg-white" data-testid={`corte-row-${c.id_caja}`}>
                  <button onClick={() => setAbierto(open ? null : c.id_caja)}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-[#FAFAFA]"
                    data-testid={`corte-toggle-${c.id_caja}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {open ? <ChevronDown className="h-4 w-4 text-[#B45309] shrink-0" /> : <ChevronRight className="h-4 w-4 text-[#71717A] shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-bold text-sm truncate flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-[#71717A]" />{c.sucursal_nombre || "Sucursal"}</p>
                        <p className="text-[11px] text-[#71717A] flex items-center gap-1.5"><User2 className="h-3 w-3" />{c.operador || "—"} · {fmtFecha(c.fecha_cierre)}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Neto entregado</p>
                      <p className="font-display font-black text-[#047857]" data-testid={`corte-neto-${c.id_caja}`}>{money(c.efectivo_neto_entregar)}</p>
                    </div>
                  </button>

                  {open && (
                    <div className="border-t border-[#e4e4e7] p-4 space-y-3 bg-[#FAFAFA]" data-testid={`corte-detalle-${c.id_caja}`}>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                        <Info icon={Banknote} color="#047857" label="Efectivo bruto" value={money(c.ingresos_efectivo)} />
                        <Info icon={CreditCard} color="#0369A1" label="Tarjeta (Point)" value={money(c.ingresos_tarjeta)} />
                        <Info label="Fondo inicial" value={money(c.fondo_inicial)} />
                        <Info label="Efectivo contado" value={c.efectivo_cierre != null ? money(c.efectivo_cierre) : "—"} />
                      </div>
                      <div className="border border-[#e4e4e7] bg-white divide-y divide-[#f1f1f3] text-sm">
                        <Row label="(+) Efectivo recaudado en turno / caja" value={money(c.ingresos_efectivo)} />
                        <Row label="(+) Fondo inicial" value={money(c.fondo_inicial)} />
                        <Row label="(−) Gastos y nóminas" value={`−${money(c.total_gastos)}`} danger />
                        <Row label="(−) Retiro de propinas" value={`−${money(c.retiro_propinas)}`} danger />
                        {Number(c.monto_factor_riesgo_aplicado || 0) > 0 && (
                          <Row label="(+) Factor de Riesgo Valet (Aporte de operador)" value={`+${money(c.monto_factor_riesgo_aplicado)}`} />
                        )}
                        <div className="flex justify-between px-4 py-2.5 bg-[#FFFBEB]">
                          <span className="text-xs uppercase tracking-wider font-black text-[#B45309]">= Total efectivo a entregar (Caja + Factor de Riesgo)</span>
                          <span className="font-display font-black text-[#B45309]">{money(c.efectivo_neto_entregar)}</span>
                        </div>
                        {diff != null && (
                          <div className="flex justify-between px-4 py-2">
                            <span className={Math.abs(diff) < 0.005 ? "text-[#047857]" : "text-[#B91C1C]"}>{Math.abs(diff) < 0.005 ? "Caja cuadrada" : diff > 0 ? "Sobrante" : "Faltante"}</span>
                            <span className={`font-mono ${Math.abs(diff) < 0.005 ? "text-[#047857]" : "text-[#B91C1C]"}`}>{diff >= 0 ? "+" : "−"}{money(Math.abs(diff))}</span>
                          </div>
                        )}
                      </div>

                      {Number(c.monto_factor_riesgo_aplicado || 0) > 0 && (
                        <div className="border border-[#10B981]/40 bg-[#ECFDF5] p-3" data-testid={`corte-factor-riesgo-${c.id_caja}`}>
                          <p className="text-[10px] uppercase tracking-wider font-black text-[#047857] mb-1.5">Factor de Riesgo Valet</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#065F46]">
                            <span>Tickets usados: <b>{c.folios_totales_turno ?? 0}</b></span>
                            <span>Cancelados/Cortesías: <b>{c.folios_cancelados_cortesia ?? 0}</b></span>
                            <span>Sujetos a factor: <b>{c.folios_factor_riesgo ?? 0}</b></span>
                          </div>
                          <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#10B981]/30">
                            <span className="text-xs uppercase tracking-wider font-black text-[#047857]">A entregar por factor</span>
                            <span className="font-display font-black text-[#047857]">{money(c.monto_factor_riesgo_aplicado)}</span>
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#52525B] mb-1">Gastos del turno ({c.gastos?.length || 0})</p>
                        {(c.gastos || []).length === 0 ? (
                          <p className="text-xs text-[#71717A]">Sin gastos registrados.</p>
                        ) : (
                          <div className="border border-[#e4e4e7] bg-white divide-y divide-[#f1f1f3]">
                            {c.gastos.map((g) => (
                              <div key={g.id} className="flex justify-between items-start px-3 py-2 text-sm gap-3" data-testid={`corte-gasto-${g.id}`}>
                                <div className="min-w-0">
                                  <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-[#B45309] border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-1.5 py-0.5">{g.categoria}</span>
                                  <p className="text-[#3f3f46] mt-0.5 break-words">{g.concepto}</p>
                                </div>
                                <span className="font-mono text-[#B91C1C] shrink-0">−{money(g.monto)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {!loading && total > 0 && (
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="cortes-paginacion">
            <p className="text-xs text-[#52525B]" data-testid="cortes-rango-info">
              Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total} cortes
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="cortes-prev-page"
                className="h-9 px-3 inline-flex items-center gap-1 border border-[#0A0A0A] text-xs font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200">
                <ChevronLeft className="h-4 w-4" /> Anterior
              </button>
              {getPageWindow(page, totalPages).map((n, i) => (
                n === "…" ? (
                  <span key={`e-${i}`} className="h-9 w-9 inline-flex items-center justify-center text-[#71717A] text-sm">…</span>
                ) : (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    data-testid={`cortes-page-${n}`}
                    className={`h-9 min-w-9 px-2 inline-flex items-center justify-center border text-sm font-bold transition-colors duration-200 ${
                      n === page ? "bg-[#FFCC00] border-[#0A0A0A] text-[#0A0A0A]" : "border-[#e4e4e7] text-[#52525B] hover:border-[#0A0A0A]"
                    }`}>
                    {n}
                  </button>
                )
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="cortes-next-page"
                className="h-9 px-3 inline-flex items-center gap-1 border border-[#0A0A0A] text-xs font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200">
                Siguiente <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// Ventana de números de página con elipsis: 1 … (p-1) p (p+1) … N
function getPageWindow(page, totalPages) {
  const pages = [];
  const add = (n) => { if (!pages.includes(n)) pages.push(n); };
  const out = [];
  add(1);
  for (let n = page - 1; n <= page + 1; n++) if (n >= 1 && n <= totalPages) add(n);
  add(totalPages);
  const sorted = pages.filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  let prev = 0;
  for (const n of sorted) {
    if (prev && n - prev > 1) out.push("…");
    out.push(n);
    prev = n;
  }
  return out;
}

function Info({ icon: Icon, color, label, value }) {
  return (
    <div className="border border-[#e4e4e7] bg-white p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#52525B]">
        {Icon ? <Icon className="h-3.5 w-3.5" style={color ? { color } : undefined} /> : null}{label}
      </div>
      <p className="font-display font-black text-[#0A0A0A] mt-0.5" style={color ? { color } : undefined}>{value}</p>
    </div>
  );
}

function Row({ label, value, danger }) {
  return (
    <div className="flex justify-between px-4 py-2">
      <span className={danger ? "text-[#B91C1C]" : "text-[#52525B]"}>{label}</span>
      <span className={`font-mono ${danger ? "text-[#B91C1C]" : ""}`}>{value}</span>
    </div>
  );
}
